import React, { useEffect, useRef } from 'react';
import Chart from 'chart.js/auto';
import { formatCurrency } from '../../utils/dataUtils';
import { useCurrency } from '../../hooks/useCurrency';
import { useTheme } from '../../hooks/useTheme';
import {
  ChartEmptyState,
  getChartTheme,
  tooltipStyle,
  barValueAxis,
  categoryAxis,
  barDataset,
  barChartHeight,
  barValueLabels,
  BAR_LABEL_PADDING
} from './chartTheme';

const MAX_PROJECTS = 15;

export const ProjectChart = ({ data, onProjectSelect }) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const currencyKey = useCurrency();
  const themeName = useTheme();

  useEffect(() => {
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    // Return early if no meaningful data
    if (!data || Object.keys(data).length === 0) {
      return;
    }

    const ctx = chartRef.current.getContext('2d');
    const theme = getChartTheme(themeName);

    // Sort projects by spend amount (descending)
    const sortedData = Object.entries(data)
      .sort((a, b) => b[1] - a[1])
      .slice(0, MAX_PROJECTS); // Limit to the top projects to avoid overcrowding

    const labels = sortedData.map(([key]) => key);
    const values = sortedData.map(([, value]) => value);

    // One colour for every project; spend that belongs to no project is neutral
    const backgroundColors = sortedData.map(([key]) => {
      if (key === 'Unassigned' || key === 'No Project Data') {
        return theme.neutral;
      }
      return theme.bar;
    });

    chartInstance.current = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: labels,
        datasets: [barDataset('Project Spend', values, backgroundColors)]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: 'y',
        layout: { padding: BAR_LABEL_PADDING },
        scales: {
          x: barValueAxis(theme),
          y: categoryAxis(25, theme)
        },
        plugins: {
          tooltip: {
            ...tooltipStyle(theme),
            callbacks: {
              label: context => 'Spend: ' + formatCurrency(context.raw)
            }
          },
          legend: {
            display: false // Hide legend since project names are on Y-axis
          },
          title: {
            display: Object.keys(data).length > MAX_PROJECTS,
            text: `Showing top ${MAX_PROJECTS} of ${Object.keys(data).length} projects`,
            position: 'bottom',
            padding: {
              top: 10
            },
            color: theme.ink.muted,
            font: {
              size: 12,
              weight: 'normal'
            }
          }
        }
      },
      plugins: [
        barValueLabels(theme),
        {
          id: 'customCanvasBackgroundColor',
          afterDatasetsDraw(chart) {
            chart.canvas.style.cursor = 'pointer';
          }
        }
      ]
    });

    // Add click event handler to the canvas
    const handleCanvasClick = (event) => {
      const points = chartInstance.current.getElementsAtEventForMode(event, 'nearest', { intersect: true }, true);
      if (points.length > 0) {
        const index = points[0].index;
        const projectName = labels[index];
        if (onProjectSelect) {
          onProjectSelect(projectName);
        }
      }
    };

    const canvas = chartRef.current;
    canvas.addEventListener('click', handleCanvasClick);

    return () => {
      if (canvas) {
        canvas.removeEventListener('click', handleCanvasClick);
      }
      if (chartInstance.current && typeof chartInstance.current.destroy === 'function') {
        chartInstance.current.destroy();
      }
    };
  }, [data, onProjectSelect, currencyKey, themeName]);

  // Determine if we have data or need to show a message
  const hasData = data && Object.keys(data).length > 0;
  const barCount = hasData ? Math.min(Object.keys(data).length, MAX_PROJECTS) : 0;

  return (
    <div style={{ height: hasData ? `${barChartHeight(barCount)}px` : '100%', position: 'relative' }}>
      {!hasData ? (
        <ChartEmptyState>No project data available</ChartEmptyState>
      ) : (
        <canvas ref={chartRef} role="img" aria-label="Bar chart of spend by project. Click a bar to see its line items." />
      )}
    </div>
  );
};
